import React from "react";
import { Modal, type ModalProps } from "antd";

export interface AppModalProps extends ModalProps {
  /** 是否显示默认的确定和取消按钮 */
  showDefaultFooter?: boolean;
}

type DocgenInfo = {
  displayName: string;
  description: string;
  props: Record<string, {
    name: string;
    required: boolean;
    description: string;
    type: { name: string; raw: string };
    defaultValue?: { value: string };
  }>;
};

type ComponentWithDocgen = React.FC<AppModalProps> & { __docgenInfo?: DocgenInfo };

/** 应用级模态框组件，基于 Ant Design Modal 封装 */
export const AppModal: React.FC<AppModalProps> = ({ 
  showDefaultFooter = true,
  ...props 
}) => {
  const footer = props.footer !== undefined 
    ? props.footer 
    : showDefaultFooter 
      ? (_: React.ReactNode, { OkBtn, CancelBtn }: { OkBtn: React.ComponentType<Record<string, unknown>>; CancelBtn: React.ComponentType<Record<string, unknown>> }) => (
          <>
            <OkBtn />
            <CancelBtn />
          </>
        )
      : null;

  return (
    <Modal
      {...props}
      footer={footer}
    />
  );
};

// 手动添加 docgen 信息
(AppModal as ComponentWithDocgen).__docgenInfo = {
  displayName: "AppModal",
  description: "应用级模态框组件，基于 Ant Design Modal 封装",
  props: {
    showDefaultFooter: {
      name: "showDefaultFooter",
      required: false,
      description: "是否显示默认的确定和取消按钮",
      type: { name: "boolean", raw: "boolean" },
      defaultValue: { value: "true" }
    },
    title: {
      name: "title",
      required: false,
      description: "模态框标题",
      type: { name: "ReactNode", raw: "React.ReactNode" }
    },
    open: {
      name: "open",
      required: false,
      description: "是否显示模态框",
      type: { name: "boolean", raw: "boolean" }
    },
    onCancel: {
      name: "onCancel",
      required: false,
      description: "取消按钮点击事件",
      type: { name: "function", raw: "(e: React.MouseEvent<HTMLElement>) => void" }
    },
    onOk: {
      name: "onOk",
      required: false,
      description: "确定按钮点击事件",
      type: { name: "function", raw: "(e: React.MouseEvent<HTMLElement>) => void" }
    },
    children: {
      name: "children",
      required: false,
      description: "模态框内容",
      type: { name: "ReactNode", raw: "React.ReactNode" }
    },
    width: {
      name: "width",
      required: false,
      description: "模态框宽度",
      type: { name: "string | number", raw: "string | number" }
    },
    centered: {
      name: "centered",
      required: false,
      description: "是否居中显示",
      type: { name: "boolean", raw: "boolean" }
    },
    maskClosable: {
      name: "maskClosable",
      required: false,
      description: "点击蒙层是否允许关闭",
      type: { name: "boolean", raw: "boolean" }
    }
  }
};
